import { useQuery } from "@tanstack/react-query";
import { api } from "../api";

export type LiveActivityEventType =
  | "new_booking"
  | "cart_recovered"
  | "impression_spike";

export interface LiveActivityEvent {
  id: string;
  occurredAt: string;
  eventType: LiveActivityEventType;
  value: number;
  count: number;
  campaignName?: string;
  location?: string;
}

export interface LiveActivityHeaderStats {
  bookings7d: number;
  value7d: number;
}

export interface LiveActivityPayload {
  headerStats: LiveActivityHeaderStats;
  events: LiveActivityEvent[];
  nextPollAfter: string;
}

export const DISCOVERY_SUBTITLE = "Discovery & Competitor";

const dayKey = (e: LiveActivityEvent) => e.occurredAt.slice(0, 10);

/**
 * The backend emits one Discovery & Competitor booking twice — once as
 * `new_booking`, once as `cart_recovered` — so the feed showed it as two rows.
 * Collapse the pair: keep the booking row, carry the discovery attribution
 * down into its subtitle. A `cart_recovered` on a day with no booking row is
 * left alone rather than silently dropped.
 *
 * Matched on calendar day alone, NOT day + value: a booking row is one DAY's
 * bookings and its `value` is the day's total, so on a two-booking day the
 * totals differ from the discovery event's value and a value-keyed match
 * would miss exactly the duplicate this fixes. `cart_recovered` is never a
 * real cart recovery here (see EVENT_LABEL in LiveActivityPanel) — it always
 * carries discovery/competitor booking value — so there is no unrelated
 * recovery event a day-wide match could swallow.
 *
 * ponytail: on a mixed day the aggregate row is attributed wholly to
 * discovery. Split the attribution when the API exposes per-booking rows.
 */
export function mergeDiscoveryBookings(
  events: LiveActivityEvent[],
): LiveActivityEvent[] {
  const discoveryDays = new Set<string>();
  const bookingDays = new Set<string>();
  for (const e of events) {
    if (e.eventType === "cart_recovered") discoveryDays.add(dayKey(e));
    if (e.eventType === "new_booking") bookingDays.add(dayKey(e));
  }
  if (discoveryDays.size === 0) return events;

  return events.flatMap((e) => {
    const day = dayKey(e);
    if (e.eventType === "cart_recovered") {
      return bookingDays.has(day) ? [] : [e];
    }
    if (e.eventType === "new_booking" && discoveryDays.has(day)) {
      return [{ ...e, campaignName: DISCOVERY_SUBTITLE }];
    }
    return [e];
  });
}

/**
 * Live Activity polling hook.
 *
 *   - `enabled` should be false when the panel is closed → no polling.
 *   - When tab is hidden, TanStack Query's refetchInterval honors
 *     refetchIntervalInBackground=false to pause automatically.
 */
export function useLiveActivity(
  userId: string | undefined,
  enabled: boolean,
) {
  return useQuery<LiveActivityPayload>({
    queryKey: ["marketing-live-activity", userId],
    queryFn: () =>
      api.marketing.liveActivity.get(userId as string) as Promise<
        LiveActivityPayload
      >,
    select: (payload) => ({
      ...payload,
      events: mergeDiscoveryBookings(payload.events ?? []),
    }),
    enabled: !!userId && enabled,
    refetchInterval: enabled ? 300_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 60_000,
  });
}
